'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { toast } from 'sonner';
import { analytics } from '@/lib/analytics';

// Service Provider Flag
export interface ServiceProviderFlagRequest {
  serviceProviderID: string;
  reason: string;
}

export interface ServiceProviderFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useServiceProviderFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ServiceProviderFlagResponse, Error, ServiceProviderFlagRequest>({
    mutationFn: ({ serviceProviderID, reason }) => {
      
      return Axios.post('/marketplace/service-providers/reports', {
        serviceProviderID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Content has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['marketplace/service-providers'] });
        queryClient.invalidateQueries({ queryKey: ['marketplace/service-providers', variables.serviceProviderID] });
        queryClient.invalidateQueries({ queryKey: ['service-provider-by-id'] });
      } else {
        const errorMessage = data.message || 'Failed to flag content';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag content';
      toast.error(errorMessage);
    },
  });
};

// Service Provider Request (ISO) Flag
export interface ServiceProviderRequestFlagRequest {
  serviceProviderRequestID: string;
  reason: string;
}

export interface ServiceProviderRequestFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useServiceProviderRequestFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ServiceProviderRequestFlagResponse, Error, ServiceProviderRequestFlagRequest>({
    mutationFn: ({ serviceProviderRequestID, reason }) => {
      
      return Axios.post('/marketplace/service-providers/requests/reports', {
        serviceProviderRequestID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Content has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['marketplace/service-providers/requests'] });
        queryClient.invalidateQueries({ queryKey: ['service-provider-request', variables.serviceProviderRequestID] });
        queryClient.invalidateQueries({ queryKey: ['service-provider-request-by-id'] });
      } else {
        const errorMessage = data.message || 'Failed to flag content';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag content';
      toast.error(errorMessage);
    },
  });
};

// Marketplace Listing Flag
export interface MarketplaceListingFlagRequest {
  listingID: string;
  reason: string;
}

export interface MarketplaceListingFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useMarketplaceListingFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<MarketplaceListingFlagResponse, Error, MarketplaceListingFlagRequest>({
    mutationFn: ({ listingID, reason }) => {
      
      return Axios.post('/marketplace/listings/reports', {
        listingID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Content has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['marketplace/listings'] });
        queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings', variables.listingID] });
        queryClient.invalidateQueries({ queryKey: ['listing-by-id'] });
      } else {
        const errorMessage = data.message || 'Failed to flag content';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag content';
      toast.error(errorMessage);
    },
  });
};

// Post Flag
export interface PostFlagRequest {
  postID: string;
  reason: string;
}

export interface PostFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const usePostFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<PostFlagResponse, Error, PostFlagRequest>({
    mutationFn: ({ postID, reason }) => {
      
      return Axios.post('/posts/reports', {
        postID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onMutate: async ({ postID }) => {
      // Store previous cache states for rollback
      const previousCaches: Record<string, any> = {};

      const updateCache = (queryKey: any[], cacheKey: string) => {
        const previousData = queryClient.getQueriesData({ queryKey });
        if (previousData.length > 0) {
          previousCaches[cacheKey] = previousData.map(([key, data]) => [key, data]);
        }

        queryClient.setQueriesData(
          { queryKey, exact: false },
          (oldData: any) => {
            if (!oldData) return oldData;
            
            // Handle ForumResponse structure (used in forums page)
            if (oldData.responseObject && Array.isArray(oldData.responseObject.rows)) {
              return {
                ...oldData,
                responseObject: {
                  ...oldData.responseObject,
                  rows: oldData.responseObject.rows.map((row: any) => {
                    if (row.id === postID) {
                      return {
                        ...row,
                        isReported: true,
                      };
                    }
                    return row;
                  }),
                },
              };
            }
            
            // Handle single post structure
            if (oldData.responseObject && oldData.responseObject.id === postID) {
              return {
                ...oldData,
                responseObject: {
                  ...oldData.responseObject,
                  isReported: true,
                },
              };
            }
            
            // Handle infinite query structure
            if (oldData.pages && Array.isArray(oldData.pages)) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => {
                  if (page.responseObject && Array.isArray(page.responseObject.rows)) {
                    return {
                      ...page,
                      responseObject: {
                        ...page.responseObject,
                        rows: page.responseObject.rows.map((row: any) => {
                          if (row.id === postID) {
                            return {
                              ...row,
                              isReported: true,
                            };
                          }
                          return row;
                        }),
                      },
                    };
                  }
                  return page;
                }),
              };
            }
            
            return oldData;
          }
        );
      };

      // Update all relevant query caches
      updateCache(['posts'], 'posts');
      updateCache(['posts', postID], `post${postID}`);
      updateCache(['posts/feed'], 'postsFeed');
      updateCache(['posts-feed'], 'postsFeedAlt');
      updateCache(['posts/my'], 'postsMy');
      updateCache(['posts', 'infinite'], 'postsInfinite');
      updateCache(['users', 'posts'], 'usersPosts');

      // Return previous caches for rollback
      return { previousCaches };
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Content has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['posts', variables.postID] });
        queryClient.invalidateQueries({ queryKey: ['posts-feed'] });
      } else {
        const errorMessage = data.message || 'Failed to flag content';
        toast.error(errorMessage);
      }
    },
    onError: (error: any, variables, context) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag content';
      toast.error(errorMessage);
      
      // Rollback all cache updates on error
      if (context?.previousCaches) {
        Object.entries(context.previousCaches).forEach(([_, cacheEntries]: [string, any]) => {
          cacheEntries.forEach(([queryKey, data]: [any, any]) => {
            queryClient.setQueryData(queryKey, data);
          });
        });
      }
    },
  });
};

// Business Flag
export interface BusinessFlagRequest {
  businessID: string;
  reason: string;
}

export interface BusinessFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useBusinessFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<BusinessFlagResponse, Error, BusinessFlagRequest>({
    mutationFn: ({ businessID, reason }) => {      
      return Axios.post('/businesses/reports', {
        businessID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Content has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['businesses'] });
        queryClient.invalidateQueries({ queryKey: ['business', variables.businessID] });
        queryClient.invalidateQueries({ queryKey: ['business-by-id'] });
      } else {
        const errorMessage = data.message || 'Failed to flag content';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag content';
      toast.error(errorMessage);
    },
  });
};

// Community Flag
export interface CommunityFlagRequest {
  groupID: string;
  reason: string;
}

export interface CommunityFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useCommunityFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CommunityFlagResponse, Error, CommunityFlagRequest>({
    mutationFn: ({ groupID, reason }) => {
      return Axios.post('/groups/reports', {
        groupID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Community has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['communities'] });
        queryClient.invalidateQueries({ queryKey: ['community', variables.groupID] });
        queryClient.invalidateQueries({ queryKey: ['communities/my'] });
      } else {
        const errorMessage = data.message || 'Failed to flag community';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag community';
      toast.error(errorMessage);
    },
  });
};

// Comment Flag
export interface CommentFlagRequest {
  commentID: string;
  reason: string;
}

export interface CommentFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useCommentFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CommentFlagResponse, Error, CommentFlagRequest>({
    mutationFn: ({ commentID, reason }) => {
      return Axios.post('/comments/reports', {
        commentID,
        reason
      }, {
        headers: getAPIRequestTokenHeaders(),
      }).then(res => {
        return res.data;
      }).catch(error => {
        throw error;
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        toast.success('Comment has been flagged successfully!');
        analytics.bugReported();

        // Invalidate relevant queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['comments'] });
        queryClient.invalidateQueries({ queryKey: ['posts', variables.commentID] });
        queryClient.invalidateQueries({ queryKey: ['posts-feed'] });
      } else {
        const errorMessage = data.message || 'Failed to flag comment';
        toast.error(errorMessage);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to flag comment';
      toast.error(errorMessage);
    },
  });
};
// User (member profile) Flag
export interface UserFlagRequest {
  userID: string;
  reason: string;
}

export interface UserFlagResponse {
  success: boolean;
  message: string | null;
  responseObject: any | null;
  statusCode: number;
}

export const useUserFlagMutation = () => {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<UserFlagResponse, Error, UserFlagRequest>({
    mutationFn: ({ userID, reason }) => {
      return Axios.post(
        '/users/reports',
        { userID, reason },
        { headers: getAPIRequestTokenHeaders() }
      )
        .then((res) => res.data)
        .catch((error) => {
          throw error;
        });
    },
    onSuccess: (data, variables) => {
      if (data?.success !== false) {
        toast.success('Report submitted');
        analytics.bugReported();
        queryClient.invalidateQueries({ queryKey: ['user', variables.userID] });
      } else {
        toast.error(data.message || 'Failed to submit report');
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to submit report';
      toast.error(message);
    },
  });
};
